import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaCheck, FaTimes, FaPhoneAlt, FaEnvelope, FaShieldAlt, FaPause, FaPlay, FaRupeeSign } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { acceptBooking, rejectBooking, startWork, pauseWork, resumeWork, workCompleted, generateArrivalOtp, verifyArrivalOtp, calculatePricing } from '../services/bookingService';
import StatusTracker from './StatusTracker';
import './JobRequestCard.css';

const ACTION_TIMEOUT_MS = 20000;

function withActionTimeout(promise, actionLabel) {
    let timer = null;

    return Promise.race([
        promise,
        new Promise((_, reject) => {
            timer = setTimeout(() => {
                reject(new Error(`${actionLabel} timed out. Please refresh and check latest status.`));
            }, ACTION_TIMEOUT_MS);
        })
    ]).finally(() => {
        if (timer) clearTimeout(timer);
    });
}

const JobRequestCard = ({ job, onUpdate }) => {
    const { currentUser } = useAuth();
    const [actionLoading, setActionLoading] = useState('');
    const [error, setError] = useState('');
    const [acceptedElsewhere, setAcceptedElsewhere] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [liveTimer, setLiveTimer] = useState('00:00:00');
    const [optimisticStatus, setOptimisticStatus] = useState(''); // Optimistic UI state
    const timerRef = useRef(null);

    // Live timer for in-progress jobs
    useEffect(() => {
        if (job.status !== 'in-progress' || !job.workSessions?.length) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }

        const tick = () => {
            const sessions = job.workSessions || [];
            let totalMs = 0;
            sessions.forEach(s => {
                const start = new Date(s.start).getTime();
                const end = s.end ? new Date(s.end).getTime() : Date.now();
                totalMs += Math.max(0, end - start);
            });
            const totalSec = Math.floor(totalMs / 1000);
            const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
            const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
            const s = String(totalSec % 60).padStart(2, '0');
            setLiveTimer(`${h}:${m}:${s}`);
        };

        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerRef.current);
    }, [job.status, job.workSessions]);

    useEffect(() => {
        // Reset temporary conflict state when live status refreshes.
        if (job.status !== 'pending') {
            setAcceptedElsewhere(false);
        }
    }, [job.id, job.status]);

    // Clear optimistic state when actual status changes from Firestore
    useEffect(() => {
        setOptimisticStatus('');
    }, [job.status]);

    // Calculate live pricing
    const livePricing = (job.status === 'in-progress' || job.status === 'work-paused' || job.status === 'work-completed')
        ? calculatePricing(job.workSessions || [], job.visitFee || 0, job.hourlyRate || 0)
        : null;

    const handleAccept = async () => {
        if (!currentUser?.uid) {
            setError('Session expired. Please login again to accept bookings.');
            return;
        }

        setActionLoading('accept');
        setOptimisticStatus('accepted');
        setError('');
        try {
            await withActionTimeout(acceptBooking(
                job.id,
                currentUser.uid,
                currentUser.displayName || 'Service Provider',
                currentUser.email || ''
            ), 'Accept request');
            setAcceptedElsewhere(false);
            // Clear optimistic state - actual status will come from Firestore
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            const message = err?.message || 'Failed to accept booking.';
            const lower = message.toLowerCase();

            // Revert optimistic state on failure
            setOptimisticStatus('');

            if (lower.includes('already accepted') || lower.includes('already')) {
                setAcceptedElsewhere(true);
                setError('Accepted by another worker. This request is no longer available.');
                onUpdate && onUpdate();
            } else {
                setError(message);
                if (lower.includes('timed out')) {
                    onUpdate && onUpdate();
                }
            }
        } finally {
            setActionLoading('');
        }
    };

    const handleReject = async () => {
        if (!window.confirm('Are you sure you want to reject this job?')) return;
        setActionLoading('reject');
        setOptimisticStatus('rejected');
        setError('');
        try {
            await withActionTimeout(rejectBooking(
                job.id,
                currentUser.displayName || 'Service Provider'
            ), 'Decline request');
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            setOptimisticStatus('');
            setError(err.message);
            if ((err?.message || '').toLowerCase().includes('timed out')) {
                onUpdate && onUpdate();
            }
        } finally {
            setActionLoading('');
        }
    };

    const handleStartWork = async () => {
        setActionLoading('start');
        setOptimisticStatus('in-progress');
        setError('');
        try {
            await withActionTimeout(startWork(job.id, currentUser.displayName || 'Service Provider'), 'Start work');
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            setOptimisticStatus('');
            setError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handleReachedSite = async () => {
        setActionLoading('otp-gen');
        setError('');
        setOtpError('');
        try {
            await withActionTimeout(generateArrivalOtp(job.id), 'Generate OTP');
            setOtpMode(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpInput.trim()) {
            setOtpError('Please enter the OTP');
            return;
        }
        setActionLoading('otp-verify');
        setOtpError('');
        try {
            await withActionTimeout(verifyArrivalOtp(
                job.id,
                otpInput,
                currentUser.displayName || 'Service Provider'
            ), 'Verify OTP');
            setOtpMode(false);
            setOtpInput('');
            onUpdate && onUpdate();
        } catch (err) {
            setOtpError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handleWorkCompleted = async () => {
        if (!window.confirm('Mark work as complete? Final bill will be generated.')) return;
        setActionLoading('complete');
        setOptimisticStatus('work-completed');
        setError('');
        try {
            await withActionTimeout(workCompleted(
                job.id,
                currentUser.displayName || 'Service Provider',
                currentUser.email || ''
            ), 'Complete work');
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            setOptimisticStatus('');
            setError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handlePauseWork = async () => {
        setActionLoading('pause');
        setOptimisticStatus('work-paused');
        setError('');
        try {
            await withActionTimeout(pauseWork(job.id, currentUser.displayName || 'Service Provider'), 'Pause work');
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            setOptimisticStatus('');
            setError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handleResumeWork = async () => {
        setActionLoading('resume');
        setOptimisticStatus('in-progress');
        setError('');
        try {
            await withActionTimeout(resumeWork(job.id, currentUser.displayName || 'Service Provider'), 'Resume work');
            setOptimisticStatus('');
            onUpdate && onUpdate();
        } catch (err) {
            setOptimisticStatus('');
            setError(err.message);
        } finally {
            setActionLoading('');
        }
    };

    const handleTrackerAction = async (nextStatus) => {
        switch (nextStatus) {
            case 'accepted':
                return handleAccept();
            case 'reached-site':
                return handleReachedSite();
            case 'in-progress':
                return handleStartWork();
            case 'work-completed':
                return handleWorkCompleted();
            default:
                break;
        }
    };

    const customerName = job.customerName || 'Unknown Customer';
    const serviceType = job.serviceName || job.serviceType || 'Service';
    const description = job.description || '';
    const location = job.address || job.location || 'No address provided';
    const date = job.preferredDate || job.date || 'Flexible';
    const time = job.preferredTime || job.time || 'Flexible';
    const price = job.estimatedPrice;
    const effectiveStatus = optimisticStatus || (acceptedElsewhere ? 'accepted-elsewhere' : job.status);

    const statusLabel = {
        'pending': 'Pending',
        'accepted': 'Accepted',
        'accepted-elsewhere': 'Accepted by another worker',
        'in-progress': 'In Progress',
        'reached-site': 'On Site',
        'work-completed': 'Completed',
        'completed': 'Completed',
        'rejected': 'Rejected',
        'cancelled': 'Cancelled',
        'payment-done': 'Paid',
    };

    return (
        <div className={`job-card ${effectiveStatus}`}>
            {/* Header */}
            <div className="job-card-header">
                <div className="customer-row">
                    <div className="customer-avatar">
                        {customerName.charAt(0).toUpperCase()}
                    </div>
                    <div className="customer-details">
                        <h4 className="customer-name">{customerName}</h4>
                        <span className="service-chip">{serviceType}</span>
                    </div>
                </div>
                <span className={`status-pill ${effectiveStatus}`}>
                    {statusLabel[effectiveStatus] || effectiveStatus}
                </span>
            </div>

            {/* Description */}
            {description && <p className="job-desc">{description}</p>}

            {/* Meta info */}
            <div className="job-meta">
                <div className="meta-item">
                    <FaMapMarkerAlt className="meta-icon" />
                    <span>{location}</span>
                </div>
                <div className="meta-item">
                    <FaCalendarAlt className="meta-icon" />
                    <span>{date}</span>
                </div>
                <div className="meta-item">
                    <FaClock className="meta-icon" />
                    <span>{time}</span>
                </div>
            </div>

            {/* Contact */}
            {(job.customerPhone || job.customerEmail) && (
                <div className="customer-contact">
                    {job.customerPhone && (
                        <a href={`tel:${job.customerPhone}`} className="contact-chip">
                            <FaPhoneAlt /> {job.customerPhone}
                        </a>
                    )}
                    {job.customerEmail && (
                        <a href={`mailto:${job.customerEmail}`} className="contact-chip">
                            <FaEnvelope /> {job.customerEmail}
                        </a>
                    )}
                </div>
            )}

            {/* Error */}
            {error && <div className="job-error">{error}</div>}

            {/* Footer — Price + Actions */}
            <div className="job-card-footer">
                <div className="price-block">
                    {job.visitFee ? (
                        <>
                            <span className="price-label">Visit Fee (incl. 1st hr)</span>
                            <span className="price-amount">₹{job.visitFee}</span>
                            <span className="price-hourly">+ ₹{job.hourlyRate || 0}/hr after</span>
                        </>
                    ) : (
                        <>
                            <span className="price-label">Est. Price</span>
                            <span className="price-amount">{price ? `₹${price}` : 'TBD'}</span>
                        </>
                    )}
                </div>

                {effectiveStatus === 'pending' && (
                    <div className="action-buttons">
                        <button
                            className="btn-action btn-decline"
                            onClick={handleReject}
                            disabled={!!actionLoading}
                        >
                            <FaTimes />
                            <span>{actionLoading === 'reject' ? 'Declining...' : 'Decline'}</span>
                        </button>
                        <button
                            className="btn-action btn-approve"
                            onClick={handleAccept}
                            disabled={!!actionLoading}
                        >
                            <FaCheck />
                            <span>{actionLoading === 'accept' ? 'Accepting...' : 'Accept'}</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Live Timer & Pricing — shown during work */}
            {(job.status === 'in-progress' || job.status === 'work-paused') && (
                <div className="work-timer-section">
                    <div className="timer-row">
                        <div className="timer-display">
                            <FaClock className="timer-icon" />
                            <span className="timer-value">{job.status === 'in-progress' ? liveTimer : `${(job.totalHoursWorked || 0).toFixed(1)} hrs`}</span>
                            <span className="timer-label">{job.status === 'in-progress' ? 'Working' : 'Paused'}</span>
                        </div>
                        {livePricing && (
                            <div className="live-cost">
                                <FaRupeeSign className="cost-icon" />
                                <span className="cost-value">₹{livePricing.totalCost}</span>
                                <span className="cost-label">Running total</span>
                            </div>
                        )}
                    </div>

                    {livePricing && livePricing.overtimeHours > 0 && (
                        <div className="overtime-badge">
                            ⚡ {livePricing.overtimeHours.toFixed(1)} hrs overtime (1.5× rate)
                        </div>
                    )}

                    <div className="work-action-buttons">
                        {job.status === 'in-progress' && (
                            <>
                                <button className="btn-work btn-pause" onClick={handlePauseWork} disabled={!!actionLoading}>
                                    <FaPause /> {actionLoading === 'pause' ? 'Pausing...' : 'Pause Work'}
                                </button>
                                <button className="btn-work btn-complete" onClick={handleWorkCompleted} disabled={!!actionLoading}>
                                    <FaCheck /> {actionLoading === 'complete' ? 'Completing...' : 'Complete Work'}
                                </button>
                            </>
                        )}
                        {job.status === 'work-paused' && (
                            <>
                                <button className="btn-work btn-resume" onClick={handleResumeWork} disabled={!!actionLoading}>
                                    <FaPlay /> {actionLoading === 'resume' ? 'Resuming...' : 'Resume Work'}
                                </button>
                                <button className="btn-work btn-complete" onClick={handleWorkCompleted} disabled={!!actionLoading}>
                                    <FaCheck /> {actionLoading === 'complete' ? 'Completing...' : 'Complete Work'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Final Bill — shown when work is completed */}
            {(job.status === 'work-completed' || job.status === 'payment-done') && job.calculatedTotal && (
                <div className="final-bill-section">
                    <h4 className="bill-title">Final Bill</h4>
                    <div className="bill-rows">
                        <div className="bill-row">
                            <span>Visit Fee (incl. 1st hr)</span>
                            <span>₹{job.visitFee || 0}</span>
                        </div>
                        {job.totalHoursWorked > 1 && (
                            <div className="bill-row">
                                <span>Additional hours ({Math.max(0, (job.totalHoursWorked - 1 - (job.overtimeHours || 0))).toFixed(1)} hrs × ₹{job.hourlyRate || 0})</span>
                                <span>₹{Math.round(Math.max(0, job.totalHoursWorked - 1 - (job.overtimeHours || 0)) * (job.hourlyRate || 0))}</span>
                            </div>
                        )}
                        {(job.overtimeHours || 0) > 0 && (
                            <div className="bill-row overtime">
                                <span>Overtime ({job.overtimeHours.toFixed(1)} hrs × ₹{Math.round((job.hourlyRate || 0) * 1.5)})</span>
                                <span>₹{Math.round(job.overtimeHours * (job.hourlyRate || 0) * 1.5)}</span>
                            </div>
                        )}
                        <div className="bill-row bill-total">
                            <span>Total ({(job.totalHoursWorked || 0).toFixed(1)} hrs)</span>
                            <span>₹{job.calculatedTotal}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Tracker */}
            {job.status !== 'pending' && job.status !== 'rejected' && (
                <StatusTracker
                    currentStatus={job.status}
                    statusHistory={job.statusHistory || []}
                    role="provider"
                    onAction={(job.status === 'in-progress' || job.status === 'work-paused') ? null : (otpMode ? null : handleTrackerAction)}
                    actionLoading={!!actionLoading}
                />
            )}

            {/* OTP Verification — Arrival */}
            {otpMode && job.status === 'accepted' && (
                <div className="otp-section">
                    <div className="otp-card">
                        <div className="otp-icon-wrap">
                            <FaShieldAlt />
                        </div>
                        <h4 className="otp-title">Verify Arrival</h4>
                        <p className="otp-desc">Ask the customer for their 4-digit arrival code</p>

                        <div className="otp-input-row">
                            <input
                                type="text"
                                className="otp-field"
                                maxLength={4}
                                placeholder="0000"
                                value={otpInput}
                                onChange={(e) => {
                                    setOtpInput(e.target.value.replace(/\D/g, ''));
                                    setOtpError('');
                                }}
                                disabled={actionLoading === 'otp-verify'}
                                autoFocus
                            />
                            <button
                                className="btn-verify"
                                onClick={handleVerifyOtp}
                                disabled={actionLoading === 'otp-verify' || otpInput.length < 4}
                            >
                                {actionLoading === 'otp-verify' ? 'Verifying...' : 'Confirm'}
                            </button>
                        </div>

                        {otpError && <div className="otp-err">{otpError}</div>}

                        <button
                            className="otp-cancel-link"
                            onClick={() => { setOtpMode(false); setOtpInput(''); setOtpError(''); }}
                        >
                            Cancel verification
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobRequestCard;
